import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { Vehicle as VehicleType } from 'models/Vehicle';
import api from 'api';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { propsWithUnits } from './data';
import utils from 'utils';
import StaticMapPreview from 'components/StaticMapPreview';
import fleetManagerSocketService from 'services/socketService/fleetManagerSocketService';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import AddVehicleTrackerModal from '../../components/AddVehicleTrackerModal';
import { Link } from 'react-router-dom';
import SyncDatVehicle from '../../components/SyncDatVehicle';

import './Vehicle.styles.scss';
import './Vehicle.styles.responsive.scss';

type VehicleProps = {
  className?: string;
};

const PropGroupCard: React.FC<{
  propGroup: Array<keyof VehicleType>;
  label?: string;
  className?: string;
  vehicle: VehicleType;
}> = ({ propGroup, className, vehicle, label }) => {
  const { t } = useTranslation();

  const classes = classNames('prop-group-card', className);

  if (!vehicle) return null;

  return (
    <div className={classes}>
      <p className="section-title">{label}</p>
      {propGroup.map((key) => {
        let text = utils.isISODateString(vehicle[key])
          ? new Date(vehicle[key] as string).toLocaleDateString()
          : vehicle[key];

        if (typeof text === 'number') {
          text = Math.round(text);
        }

        return (
          <div className="key-pair" key={key}>
            <label>{t(key)}</label>
            <p>
              {text as ReactNode} {propsWithUnits?.[key]}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const Vehicle: React.FC<VehicleProps> = (props) => {
  const { className } = props;
  const [vehicle, setVehicle] = useState<VehicleType>();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [modal, modalRef] = useCallbackRef<OverlayRef>(null);

  const classes = classNames('vehicle', className);

  const fetchVehicle = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.vehicle.getVehicle(id);
      setVehicle(data);
      data.lastLocation && setCurrentLocation(data.lastLocation);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const [currentLocation, setCurrentLocation] = useState<
    [number, number] | null
  >(vehicle?.lastLocation || null);

  useEffect(() => {
    const updateLocation = ({
      location,
      vehicleId,
    }: {
      location: [number, number];
      vehicleId: string;
    }) => {
      if (vehicleId !== vehicle._id) return;
      setCurrentLocation(location);
    };

    fleetManagerSocketService.addListener(
      LISTEN_EVENTS.LOCATION_UPDATE,
      updateLocation,
    );
    return () => {
      fleetManagerSocketService.removeListener(
        LISTEN_EVENTS.LOCATION_UPDATE,
        updateLocation,
      );
    };
  }, [vehicle]);

  const renderObjectData = useCallback(
    (object: Record<string, any>, props: string[]): Array<ReactNode> =>
      !!object &&
      props
        .map((key) => {
          if (['string', 'number'].includes(typeof object[key])) {
            if (!object[key]) return null;
            let text = utils.isISODateString(object[key])
              ? new Date(object[key]).toLocaleDateString()
              : object[key];

            if (typeof text === 'number') {
              text = Math.round(text);
            }

            return (
              <div className="vehicle__property" key={key}>
                <span className="vehicle__property__key">{t(key)}</span>
                <span className="vehicle__property__value">
                  {text} {propsWithUnits?.[key]}
                </span>
              </div>
            );
          } else return renderObjectData(object[key], props);
        })
        .filter(Boolean),
    [t],
  );

  useEffect(() => {
    id && fetchVehicle();
  }, [fetchVehicle, id]);

  if (isLoading) return <Loader />;

  const locationUsed =
    vehicle?.tracker?.location && vehicle?.tracker?.location.length
      ? vehicle?.tracker?.location
      : currentLocation;

  return (
    <div className={classes}>
      <div className="vehicle__row ">
        <Link to="/dash/vehicles" replace>
          {t('back-navigation')}
        </Link>

        <div>
          {vehicle && !vehicle.isSyncedWithDat && (
            <SyncDatVehicle vehicleId={vehicle._id} onSuccess={setVehicle} />
          )}
        </div>
      </div>

      <div className="vehicle-page-grid">
        <div className="map-container">
          {locationUsed && <StaticMapPreview coordinates={locationUsed} />}
          <div className="vehicle-content-card ">
            <span className="page-col">
              <p className="label-location">{t('lastKnownLocation')}:</p>
              <p>{vehicle?.tracker?.address || vehicle?.address}</p>
            </span>
          </div>
        </div>

        {vehicle?.images
          .map((img, index) =>
            index < 2 ? (
              <img
                className="grid-image"
                key={img.name}
                src={img.url}
                alt={img.name}
              />
            ) : null,
          )
          .filter(Boolean)}

        {vehicle?.tracker ? (
          <PropGroupCard
            label={t('tracker-info')}
            className="tracker-card-info"
            propGroup={
              ['imei', 'msisdn', 'trackerModel', 'registrationStatus'] as any
            }
            vehicle={vehicle.tracker as unknown as VehicleType}
          />
        ) : (
          <div className="vehicle-content-card">
            <p>{t('trackerNotSet')}</p>
            <Button onClick={() => modal?.open()}>
              {t('addTrackerToVehicle')}
            </Button>
          </div>
        )}
        <PropGroupCard propGroup={['vin', 'licensePlate']} vehicle={vehicle} />
        <PropGroupCard propGroup={['brand', 'model']} vehicle={vehicle} />
        <PropGroupCard
          propGroup={['engineType', 'equipmentType']}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={[
            'avgFuelConsumption',
            'avgFuelConsumptionWltp',
            'batteryCapacity',
          ]}
          vehicle={vehicle}
        />

        <PropGroupCard
          propGroup={[
            'annualAgreedKilometers',
            'annualForecastKilometers',
            'totalKm',
          ]}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={['wheelSizeInch', 'vehicleWeight', 'fuelTankVolumeLiter']}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={['emissionCO2', 'pollutionClass']}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={['lastServiceDate', 'nextServicePlanned']}
          vehicle={vehicle}
        />

        <PropGroupCard
          propGroup={[
            'monthlyServiceFee',
            'monthlyLeasingFee',
            'leasingTimeEndMonthDue',
          ]}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={['calculatedValueAtDate', 'actualValueAtDate']}
          vehicle={vehicle}
        />

        <PropGroupCard
          propGroup={['averageSpeed', 'currentSpeed']}
          vehicle={vehicle}
        />
        <PropGroupCard
          propGroup={['engineHours', 'parkingHours']}
          vehicle={vehicle}
        />

        <PropGroupCard
          propGroup={['damages', 'department']}
          vehicle={vehicle}
        />
      </div>

      {!!vehicle && !vehicle?.trackerId && (
        <AddVehicleTrackerModal
          vehicleId={vehicle._id}
          ref={modalRef}
          onSuccess={(vehicle: VehicleType) => setVehicle(vehicle)}
        />
      )}
    </div>
  );
};

export default Vehicle;
